import {Knowledge, UploadProgress} from '../types';
import {startSession, resetSession} from '../session';
import {apiUrl, payloadError, REQUEST_HEADERS} from '../http';

const MAX_UPLOAD_BYTES = 5_000_000;
const ALLOWED_EXTENSIONS = new Set(['txt', 'md', 'pdf', 'docx']);

export function validateUpload(file: File): void {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error('Please keep files under 5 MB.');
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXTENSIONS.has(extension)) throw new Error('Please upload a TXT, Markdown, PDF, or DOCX file.');
  if (!file.size) throw new Error('This file is empty. Choose a document with readable text.');
}

function completeUpload(xhr: XMLHttpRequest, resolve: (value: Knowledge) => void, reject: (error: Error) => void): void {
  if (xhr.status === 401) resetSession();
  if (xhr.status < 200 || xhr.status >= 300) {
    reject(payloadError(xhr.response, 'The document could not be uploaded. Please try again.')); return;
  }
  const result = xhr.response as Knowledge | null;
  if (!result?.id) { reject(new Error('The upload response was incomplete. Please refresh before trying again.')); return; }
  resolve(result);
}

function transfer(file: File, progress: (value: UploadProgress) => void): Promise<Knowledge> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = event => progress({phase: 'uploading', percent: event.lengthComputable ? Math.round(event.loaded / event.total * 100) : null});
    xhr.upload.onload = () => progress({phase: 'processing', percent: null});
    xhr.onload = () => completeUpload(xhr, resolve, reject);
    xhr.onerror = () => reject(new Error('The upload connection failed. Your selected file is still available to retry.'));
    xhr.ontimeout = () => reject(new Error('The upload took too long. Check the knowledge list before retrying, or use a smaller file.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.open('POST', apiUrl('/knowledge/upload'));
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.timeout = 120000;
    xhr.setRequestHeader('X-TriageFlow-Request', REQUEST_HEADERS['X-TriageFlow-Request']);
    const body = new FormData(); body.append('file', file);
    progress({phase: 'uploading', percent: 0});
    xhr.send(body);
  });
}

export async function uploadKnowledge(file: File, progress: (value: UploadProgress) => void): Promise<Knowledge> {
  validateUpload(file);
  await startSession();
  return transfer(file, progress);
}